import { ChangeDetectionStrategy, Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { filter, flatMap, map, take, switchMap, tap } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { set, get, sortBy, isEmpty, find, concat} from 'lodash';
import { Subject } from 'rxjs/Subject';

import { ConfigurationStoreHandler } from '../../../configuration/services/configuration-store-handler.service';
import { selectCurrentDiagnosticOrder } from '@ispm/test/store/diagnostic-order.selector';
import { IDefinition } from '../../../configuration/interfaces/definition.interface';
import {
  ICloseReturnType,
  PlaceholderChildComponent
} from '@ispm/layout/classes/placeholder-child-component.abstract';
import { modalResponse } from '@ispm/layout/constants/modal-responses.enum';
import { UserActionCreatorService } from '../../services/user-action-creator.service';
import { UserService } from '../../services/user.service';
import { Patient } from '../../classes/patient.class';
import { ResourceReference } from '@ispm/core/classes/resource-reference.class';
import { PatientPermissions } from '@ispm/shared/constants/permission-schema.constants';
import { Widget } from '@ispm-client/modules/widgets/decorator/widget.decorator';
import { IWidgetItem } from '@ispm-client/modules/widgets/interfaces/widget-item.interface';
import { IPatientEditingPayload } from './interfaces/patient-editing.interface';
import { FormHandlerService } from '@ispm/dynamic-form/services/form-handler.service';
import { AgregationService } from '@ispm-client/modules/aggregator/services';
import { Ifacillity } from '../../../../modules/aggregator/interfaces';

@Component({
  selector: 'gen-patient-editing',
  templateUrl: './patient-editing.component.html',
  styleUrls: ['./patient-editing.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
@Widget('PatientEditing')
export class PatientEditingComponent extends PlaceholderChildComponent implements OnInit, IWidgetItem<IPatientEditingPayload> {
  widgetUpdateValueCallback: (value) => void;
  public widgetConfigPayload: IPatientEditingPayload;
  public currentPaientMergedIdentifier: any;

  set widgetFormInstanceSubmitted(submitted: boolean){
    if(submitted){
      this.submit();
    }
  }

  set widgetConfig(widgetConfig) {
    this.widgetConfigPayload = widgetConfig.widgetPayload;
    this.payload = {};
    this.payload.mode = this.widgetConfigPayload.mode;
  }

  set widgetData(widgetData: any) {
    this.patient = widgetData || new Patient();
    this._changeDetector.markForCheck();
  }

  constructor(
    private _store: Store<any>,
    private _userAC: UserActionCreatorService,
    private _userService: UserService,
    private _changeDetector: ChangeDetectorRef,
    private _configurationStoreHandler: ConfigurationStoreHandler,
    private _aggregatorService: AgregationService
  ) {
    super();
    this.definition$ = this._configurationStoreHandler.getCurrentConfig().pipe(take(1));
  }

  private static readonly PATIENT_BASIC_DEFAULT_FORM_ID = 'PATIENT-EDITING-PERSONAL-DATA-DEFAULT';
  private static readonly CONTACT_INFO_DEFAULT_FORM_ID = 'PATIENT-EDITING-CONTACT-INFO-DEFAULT';

  public userAllowToEdit: boolean;
  private patientEditingFormId: string;
  public patientDetailsFormId: string;
  private patientEditingFormIdEditMode: string;
  public contactInfoFormId: string;
  public patient: Patient;
  public validationArray: [boolean, boolean, boolean] = [null, null, null];
  public submitted: boolean = false;
  public changeApplied: boolean = false;
  public editMode: boolean;
  public showEditToolbar: boolean;
  public showEditButton: boolean;
  public formOverrides: { [key: string]: any } = { };
  public formEditable: boolean = false;
  public updateContextSubjects: string = '';
  public updateContextTypes: string = '';
  public patientEditingFormTitle: string;
  public patientEditingFormEditingStatus: boolean = null;
  public contactInfoFormTitle: string;
  public contactInfoFormEditingStatus: boolean = null;
  public permissionKey: PatientPermissions;

  public hasPermissions: boolean = false;
  public resetForm: boolean = false;
  private definition$: Observable<IDefinition>;
  private _changed: boolean;
  public orgLevelFormConfig: boolean; // set to true if patient info form was taken from org config
  public facilityData: any[] = [];
  private destroyed$: Subject<any> = new Subject();
  public facilityConfig: any;
  private isFacilityEnabled:boolean = false;
  private currentActiveFacilities: Array<Ifacillity>;

  static isEditable(config): boolean {
    const overrides = get(config, 'stepConfig.fieldsValidation.override', []);
    return get(config, 'stepConfig.fieldsValidation.isEditable', true) && overrides.every(form => form.formEditable !== false);
  }

  ngOnInit() {
    super.ngOnInit();
    this.initOrganizationForms();
    if (this.widgetConfigPayload && this.widgetConfigPayload.mode) {
      if (this.widgetConfigPayload.mode === "CREATE") {
        this.payload.mode = MODES.CREATE;
      } else {
        this.payload.mode = MODES.UPDATE;
      }
    }
    this._store.select('users', 'currentPatientIdentifiers').subscribe(identifierData => {
      this.currentPaientMergedIdentifier = identifierData;
    })
    this.initPatientData();
    this.initFacilityData();
  }

  beforeClose(_modalResponse?: modalResponse): void | ICloseReturnType {
    if (this.payload.mode === MODES.CREATE) {
      return this.submit()
        .then(resp => ({ isValid: !!resp, patient: resp, changed: true}))
        .catch(err => ({ isValid: false}));
    }
    else {
      return { isValid: true, patient: this.patient, changed: this._changed};
    }
  }

  save() {
    return this.submit().then(resp => {
      if (!resp) {
        return;
      }

      this._changed = true;
      this.formEditable = false;
      this.submitted = true;
      this.patient = resp;
      this.patientDetailsFormId = this.patientEditingFormId;
      this.showEditToolbar = false;
      this._changeDetector.markForCheck();
    });
  }

  editClick(){
    this.formEditable = true;
    this.editMode = !this.orgLevelFormConfig; //In org level configuration form should stay in editMode = false
    this.showEditToolbar = true;
    this.patientDetailsFormId = this.patientEditingFormIdEditMode || this.patientEditingFormId;
  }

  initOrganizationForms() {
    this._userAC.getStoredOrganizationConfiguration()
      .pipe(
        filter(org => !!org),
        take(1)
      ).subscribe((org) => {
      const basicFormConf = get(org, 'forms[\'PATIENT_BASIC_INFO\']', { formId: PatientEditingComponent.PATIENT_BASIC_DEFAULT_FORM_ID});
      const basicFormConfEditMode = get(org, 'forms[\'PATIENT_BASIC_INFO_EDIT\']', basicFormConf); //If no special form wass defined for edit, use the basic form. It is needed when edit form is different from non edit form (for example, when only part of the fields should be editable)
      const contactFormConf = get(org, 'forms[\'PATIENT_CONTACT_INFO\']', { formId: PatientEditingComponent.CONTACT_INFO_DEFAULT_FORM_ID});

      this.orgLevelFormConfig = basicFormConf.formId != PatientEditingComponent.PATIENT_BASIC_DEFAULT_FORM_ID;
      this.patientEditingFormId = basicFormConf.formId;
      this.patientDetailsFormId = this.payload.mode === MODES.CREATE
        ? this.patientEditingFormId
        : basicFormConfEditMode.formId
      ;
      this.patientEditingFormIdEditMode = basicFormConfEditMode.formId;
      this.patientEditingFormTitle = basicFormConf.title || 'Personal data';
      this.contactInfoFormId = contactFormConf.formId;
      this.contactInfoFormTitle = contactFormConf.title || 'Contact info';
    });
  }

  initPatientData() {
    switch (this.payload.mode) {
      case MODES.UPDATE:
        this.editMode = true;
        this.showEditButton = true;
        this.permissionKey = PatientPermissions['PATIENT.MODIFY'];
        const updateContexts = [];
        updateContexts.push(new ResourceReference(this.payload.patientId, 'Patient'));
        updateContexts.forEach((ctx: ResourceReference) => {
          this.updateContextSubjects += `${ctx.reference},`;
          this.updateContextTypes += `${ctx.resourceType},`;
        });
        this.setFormOverrideConfigs()
          .pipe(
            switchMap(() => this.setPatient())
          )
          .subscribe();
        break;

      case MODES.CREATE:
        this.editMode = false;
        this.formEditable = true;
        this.showEditButton = false;
        this.permissionKey = PatientPermissions['PATIENT.CREATE'];
        this.patientEditingFormEditingStatus = true;
        this.contactInfoFormEditingStatus = true;
        if (this.payload.orgId && this.payload.orgName) {
          this.patient = Patient.Factory(this.payload.orgId, this.payload.orgName);
          this._changeDetector.markForCheck();
        } else {
          this._userAC.getUserInfo()
            .pipe(
              filter(org => !!org),
              take(1),
              map(user => {
                if (user.role.toUpperCase() === 'GENOMICSADMINGROUP') {
                  return get(user, 'organization');
                } else {
                  return get(user, 'info.practitionerRole[0].managingOrganization');
                }
              }),
              switchMap((orgId: string) => this._userAC.getOrganizationConfiguration(orgId))
            ).subscribe(org => {
            this.patient = Patient.Factory(org.id, org.name);
            this._changeDetector.markForCheck();
          });
        }
        break;
    }
  }

  initFacilityData() {
    this.facilityConfig = config;
    this._aggregatorService.getFacilities().takeUntil(this.destroyed$).subscribe((data) => {
      this.isFacilityEnabled = get(data, 'isFacilityEnabled');
      this.currentActiveFacilities = get(data, 'currentActiveFacilities');
      if(!isEmpty(this.currentActiveFacilities)){
          this.buildFacilitySelection();
      }
    });
  }

  buildFacilitySelection(){
    if(this.currentActiveFacilities.length > 0) {
      this.currentActiveFacilities = sortBy(this.currentActiveFacilities, this.facilityConfig.sortTableAttribute);
      this.patient['authorization'] = this.patient['authorization'] || [];
      this.currentActiveFacilities = this.currentActiveFacilities.map((value) => {
          const facilityAuth = find(this.patient.authorization, (item) => {
            return get(item,'reference') === get(value,'id');
          });
          value.selected = !!facilityAuth;
          if(get(facilityAuth,'source')){
            value['source'] = get(facilityAuth,'source');
          }
          return value;
        });
      this.updateData(this.currentActiveFacilities);
      this.facilityData = this.currentActiveFacilities;
      this.triggerChangeDetector();
    }
  }

  setPatient() {
    return this._userAC.getPatient(this.payload.patientId).pipe(
      map(patient => {
        patient["aggIdentifier"] = this.currentPaientMergedIdentifier;
        this.patient = Patient.Clone(patient);
        this._changeDetector.markForCheck();
        this._changeDetector.detectChanges();
        return this.patient;
      })
    );
  }

  reset() {
    this.formEditable = false;
    this.changeApplied = false;
    this.setPatient().subscribe(() => {
      this.resetForm = true;
      this._changeDetector.markForCheck();
    });
  }

  setFormOverrideConfigs() {
    this.formOverrides = { };
    return this.getStepData()
      .pipe(
        take(1),
        tap(({ diagnosticOrder}) => this.checkAssignee(diagnosticOrder)),
        map(config => {
          this.formOverrides = FormHandlerService.BuildOverrides(config.stepConfig, this.patientEditingFormId, this.contactInfoFormId);
          this.patientEditingFormEditingStatus = this.isFormEditable(this.patientEditingFormId);
          this.contactInfoFormEditingStatus = this.isFormEditable(this.contactInfoFormId);
          this.updateEditableAllow(PatientEditingComponent.isEditable(config));
        })
      );
  }

  updatePatient(patientObject, fields: Array<string>) {
    this.changeApplied = true;
    fields.forEach(field => {
      set(this.patient, field, get(patientObject, field));
    });
  }

  submit(): Promise<any> {
    this.submitted = true;
    if(!isEmpty(this.facilityData)){
      this.validateFacility(this.facilityData);
    } else {
      this.validationArray[2] = true;
    }
    this._changeDetector.markForCheck();
    if (this.validationArray.every(valid => !!valid)) {
      if(this.widgetUpdateValueCallback) this.widgetUpdateValueCallback(this.patient)
      return this.payload.mode === MODES.UPDATE
        ? this._userService.updatePatient(this.patient).toPromise()
        : this._userService.createPatient(this.patient).toPromise();
    } else {
      return Promise.resolve(null);
    }
  }

  getStepData() {
    return this._configurationStoreHandler
      .getConfigurationForCurrentStep()
      .pipe(
        take(1),
        flatMap(stepConfig => {
          return this._store.select(selectCurrentDiagnosticOrder).pipe(
            take(1),
            map(diagnosticOrder => {
              return {
                diagnosticOrder,
                stepConfig
              };
            })
          );
        })
      );
  }

  checkAssignee(diagnosticOrder) {
    return this._userAC.getCurrentUserId()
      .subscribe(userId => this.updateEditableAllow(userId === diagnosticOrder.performer));
  }

  updateEditableAllow(allowed: boolean) {
    if (this.userAllowToEdit === undefined) {
      this.userAllowToEdit = allowed;
    } else {
      this.userAllowToEdit = this.userAllowToEdit && allowed;
    }
  }

  isFormEditable(formId) {
    return get(this.formOverrides, `${formId}.${FormHandlerService.FORM_CONFIG_KEY}.formEditable`, true);
  }

  public updateData(dataArray: any): void {
    if (dataArray) {
      const authorizationData = this.validateFacility(dataArray).map(value => {
        var authData = {};
        authData['reference'] = value.id;
        authData['resourceType'] = 'Organization';
        authData['subResourceType'] = {code:'facility',display:'Facility'};
        if(get(value,'source')){
          authData['source'] = get(value,'source');
        }
        return authData;
      });
      this.patient['authorization'] = this.patient['authorization'] || [];
      this.patient.authorization = this.patient.authorization.filter((item) => {
        return get(item,'subResourceType.code','') !== 'facility';
      });
      if(!isEmpty(authorizationData)) {
        this.patient.authorization = concat(this.patient.authorization, authorizationData);
      }
    }
  }

  private validateFacility(dataArray) {
    const selectedFacilities = dataArray.filter(data => data.selected == true);
    if(selectedFacilities && !isEmpty(selectedFacilities)) {
      this.validationArray[2] = true;
    } else {
      if(!this.submitted) {
        this.validationArray[2] = true;
      } else {
        this.validationArray[2] = false;
      }
    }
    return selectedFacilities;
  }

  private triggerChangeDetector() {
    this._changeDetector.detectChanges();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}

export enum MODES {
  'CREATE' = 'CREATE',
  'UPDATE' = 'UPDATE'
}

const config = {
  formId: 'ISPM-ADMIN-FACILITY-ASSOCIATE-WIDGET-FORM',
  title: 'Facilities',
  sortTableAttribute: 'name',
  selectOneFacility:'Selecting at least one facility is required'
}
